import { groupBySection, packColumns, type MenuBlock } from '../paginate.js';
import { featuredItem } from './cafe-panels.js';
import {
  bilingual,
  description,
  hasTitle,
  image,
  listColumns,
  listCss,
  listItem,
  priceBlock,
  sectionSubtitle,
  sectionTitle,
  type MenuTemplate,
  type RenderContext,
} from './common.js';

/**
 * Fine dining: black with a thin gold double frame, centred serif lists, and the featured dish
 * in a gold arch between the two columns.
 */

const S = '.mb-t-black-gold';

function block(ctx: RenderContext, b: MenuBlock): string {
  const head = hasTitle(b.section)
    ? `<div class="bg-head">${sectionTitle(ctx, b.section, 'bg-title')}${b.continued ? '' : sectionSubtitle(ctx, b.section, 'bg-sub')}</div>`
    : '';
  return `<div class="bg-sec">${head}${b.items.map((i) => listItem(ctx, i, 'bg', false)).join('')}</div>`;
}

export const blackGold: MenuTemplate = {
  id: 'black-gold',
  name: 'Black & gold',
  description: 'Black with a fine gold frame, centred serif lists and the chef’s dish in a gold arch. For refined pasta and grill menus.',
  defaultTheme: {
    backgroundColor: '#0d0c0b',
    surfaceColor: '#171512',
    textColor: '#e9e1d2',
    primaryColor: '#f6efe2',
    accentColor: '#c9a45c',
    fontId: 'classic',
    backgroundMediaId: null,
  },
  variants: [
    { id: 'gold', name: 'Gold', theme: {} },
    { id: 'champagne', name: 'Champagne', theme: { accentColor: '#e3cc9b', backgroundColor: '#141312' } },
    { id: 'emerald', name: 'Emerald', theme: { backgroundColor: '#0b2a20', surfaceColor: '#103327', accentColor: '#d6b56e' } },
    { id: 'burgundy', name: 'Burgundy', theme: { backgroundColor: '#2a0d12', surfaceColor: '#36121a', accentColor: '#d4af6a' } },
  ],
  cuisines: ['pasta'],
  uses: { featured: true, sectionImages: false, brand: true, decorations: [] },

  paginate(board, orientation) {
    const portrait = orientation === 'portrait';
    const featured = featuredItem(board);
    return packColumns(
      groupBySection(board, featured?.id),
      listColumns(board, {
        count: 2,
        capacity: portrait ? (featured ? 60 : 94) : 48,
        heading: 4.6,
        item: { nameEm: 1.3, descEm: 0.85, perLine: portrait ? 40 : 38, gap: 1.6 },
      }),
      { balance: true },
    );
  },

  render(ctx) {
    const { board } = ctx;
    const item = featuredItem(board);
    const arch = item
      ? [
          '<div class="bg-feature">',
          `<div class="bg-arch">${image(ctx, item.imageMediaId, 'bg-arch-img')}</div>`,
          bilingual(ctx, item.title, item.titleAr, 'bg-feature-name'),
          description(ctx, item, 'bg-feature-desc'),
          `<div class="bg-feature-price">${priceBlock(ctx, item, 'bg')}</div>`,
          '</div>',
        ].join('')
      : '';
    const header = [
      '<div class="bg-header">',
      bilingual(ctx, board.brand.name, board.brand.nameAr, 'bg-brand'),
      bilingual(ctx, board.settings.title ?? board.name, board.settings.titleAr, 'bg-heading'),
      '<div class="bg-ornament"><i></i>◆<i></i></div>',
      '</div>',
    ].join('');
    const [left = [], right = []] = ctx.page.columns;
    const col = (blocks: MenuBlock[]) => `<div class="bg-col">${blocks.map((b) => block(ctx, b)).join('')}</div>`;
    return `<div class="bg-frame">${header}<div class="bg-grid${arch ? '' : ' bg-no-feature'}">${col(left)}${arch}${col(right)}</div></div>`;
  },

  css: `
${S}{padding:1.8em;background-image:radial-gradient(ellipse at 50% 0%,rgba(255,255,255,.05),transparent 60%)}
${S} .bg-frame{flex:1;min-height:0;display:flex;flex-direction:column;padding:2.6em 3.2em 2.4em;
  border:.12em solid var(--mb-accent);outline:.12em solid var(--mb-accent);outline-offset:-.8em}
${S} .bg-header{text-align:center;margin-bottom:1.6em}
${S} .bg-brand{font-size:.95em;letter-spacing:.5em;text-transform:uppercase;color:var(--mb-accent)}
${S} .bg-heading{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:3.6em;color:var(--mb-primary);
  font-variant:small-caps;letter-spacing:.06em;line-height:1.1}
${S} .bg-heading.mb-2nd{font-size:2.2em}
${S} .bg-ornament{display:flex;align-items:center;justify-content:center;gap:.8em;color:var(--mb-accent);font-size:.9em;margin-top:.4em}
${S} .bg-ornament i{width:6em;height:.08em;background:var(--mb-accent)}
${S} .bg-grid{flex:1;min-height:0;display:grid;grid-template-columns:1fr 26em 1fr;gap:3.4em}
${S} .bg-no-feature{grid-template-columns:1fr 1fr}
${S}.mb-portrait .bg-grid{grid-template-columns:1fr 1fr;grid-template-rows:auto 1fr;column-gap:3em}
${S}.mb-portrait .bg-feature{grid-column:1 / span 2;grid-row:1}
${S}.mb-portrait .bg-col{grid-row:2;justify-content:flex-start}
${S} .bg-col{min-width:0;display:flex;flex-direction:column;justify-content:center;gap:2em;text-align:center}
${S} .bg-head{margin-bottom:1em}
${S} .bg-title{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:1.9em;color:var(--mb-accent);
  font-variant:small-caps;letter-spacing:.12em}
${S} .bg-title.mb-2nd{font-size:1.4em;letter-spacing:0}
${S} .bg-sub{font-style:italic;opacity:.7;font-size:.95em}
${listCss(S, 'bg', { name: '1.3em', price: '1.15em', desc: '.85em', leader: '0' })}
${S} .bg-item{margin-bottom:1.3em}
${S} .bg-line{justify-content:center;flex-wrap:wrap;gap:.2em .8em}
${S} .bg-name{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);color:var(--mb-primary);font-variant:small-caps;letter-spacing:.06em}
${S} .bg-price{color:var(--mb-accent);font-weight:600;letter-spacing:.05em}
${S} .bg-pricebox{flex-direction:row;align-items:baseline;gap:.5em}
${S} .bg-desc{font-style:italic}
${S} .bg-feature{display:flex;flex-direction:column;align-items:center;text-align:center;min-height:0}
${S}.mb-portrait .bg-feature{margin-bottom:2em}
${S} .bg-arch{width:100%;aspect-ratio:3/3.8;border-radius:50% 50% 0 0/38% 38% 0 0;overflow:hidden;
  border:.15em solid var(--mb-accent);padding:.5em;background:var(--mb-bg)}
${S}.mb-portrait .bg-arch{width:26em}
${S} .bg-arch-img{width:100%;height:100%;object-fit:cover;display:block;border-radius:50% 50% 0 0/38% 38% 0 0}
${S} .bg-feature-name{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:1.9em;color:var(--mb-primary);
  margin-top:.8em;font-variant:small-caps;letter-spacing:.05em}
${S} .bg-feature-name.mb-2nd{font-size:1.4em;margin-top:.2em}
${S} .bg-feature-desc{font-style:italic;opacity:.75;margin-top:.3em}
${S} .bg-feature-price{margin-top:.5em;font-size:1.3em}
`,
};
